/**
 * Early ResizeObserver error suppression
 * This file should be imported at the very top of index.js to run before React starts
 */

// Immediately suppress ResizeObserver errors - run immediately on load
const originalErrorHandler = window.onerror;
const originalUnhandledRejection = window.onunhandledrejection;
const originalConsoleError = console.error;

// Override console.error immediately and aggressively
console.error = function(...args) {
  const message = String(args[0] || '');
  if (message.toLowerCase().includes('resizeobserver') || 
      args.some(arg => String(arg).toLowerCase().includes('resizeobserver'))) {
    // Completely suppress ResizeObserver console errors
    return;
  }
  originalConsoleError.apply(console, args);
};

// Override ResizeObserver to prevent errors at the source
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      let ticking = false;
      
      // Debounced callback using requestAnimationFrame to prevent loop errors
      const debouncedCallback = (entries, observer) => {
        if (!ticking) {
          requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              // Suppress ResizeObserver errors silently
              const errorMsg = String(error.message || '').toLowerCase();
              if (errorMsg.includes('resizeobserver') || 
                  errorMsg.includes('loop limit exceeded') ||
                  errorMsg.includes('undelivered notifications')) {
                return;
              }
              // Re-throw non-ResizeObserver errors
              throw error;
            } finally {
              ticking = false;
            }
          });
          ticking = true;
        }
      };
      
      super(debouncedCallback);
    }
  };
}

// Immediately suppress ResizeObserver errors
(function() {
  
  window.onerror = function(message, source, lineno, colno, error) {
    // Check if this is a ResizeObserver error
    const messageStr = String(message || '').toLowerCase();
    if (messageStr.includes('resizeobserver')) {
      // Suppress the error completely
      return true;
    }
    
    // Call original handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  window.onunhandledrejection = function(event) {
    const reason = String(event.reason || '').toLowerCase();
    const reasonMessage = String(event.reason?.message || '').toLowerCase();
    
    if (reason.includes('resizeobserver') || reasonMessage.includes('resizeobserver')) {
      event.preventDefault();
      return;
    }
    
    // Call original handler for other rejections
    if (originalUnhandledRejection) {
      return originalUnhandledRejection.call(this, event);
    }
  };

  // Add event listeners as backup
  window.addEventListener('error', function(event) {
    if (event.message && (
      event.message.includes('ResizeObserver') ||
      event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
      event.message.includes('ResizeObserver loop limit exceeded')
    )) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return true;
    }
  }, true); // Use capture phase

  window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && 
        event.reason.message && 
        event.reason.message.includes('ResizeObserver')) {
      event.preventDefault();
    }
  }, true); // Use capture phase

  console.log('ResizeObserver error suppression initialized early');
})();
